import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    ReactCompareSlider,
    ReactCompareSliderImage,
} from "react-compare-slider";
import SingleSelectDropdown from "../../common/utils/SingleSelectDropdown";
import DateIcon from "../../assets/images/svgs/Date.svg?react";
import { Close, CompactIcon, ExpandIcon } from "../../assets/images/svgs/Icons";
import "./CompareModal.css";
import { RESIZE_OPTIONS } from "../../common/constants/orderOptions";


const updateResizeParam = (url, resize) => {
    if (!url) return url;

    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set("resize", resize);
    return parsedUrl.toString();
};

const CompareModal = ({
    leftImage,
    rightImage,
    onClose,
    leftDate,
    rightDate,
}) => {
    const [resize, setResize] = useState(256);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [scale, setScale] = useState(1);
    const [origin, setOrigin] = useState({ x: 50, y: 50 });
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const zoomWrapperRef = useRef(null);

    const formatDate = (date) =>
        date ? new Date(date).toLocaleDateString() : "—";

    const leftImageUrl = useMemo(
        () => updateResizeParam(leftImage, resize),
        [leftImage, resize]
    );

    const rightImageUrl = useMemo(
        () => updateResizeParam(rightImage, resize),
        [rightImage, resize]
    );

    const handleToggleFullscreen = () => {
        setIsFullscreen((prev) => !prev);
    };

    const handleWheelZoom = (e) => {
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        setScale((prev) => {
            const speed = prev > 8 ? 0.08 : 0.15;
            const delta = e.deltaY < 0 ? speed : -speed;
            const newScale = Math.min(Math.max(prev + delta, 1), 20);
            
            // Update origin only when starting to zoom from base scale
            if (prev === 1 && newScale > 1) {
                setOrigin({ x, y });
            }
            
            // Reset origin and pan when returning to base scale
            if (newScale === 1) {
                setOrigin({ x: 50, y: 50 });
                setPan({ x: 0, y: 0 });
            }
            
            return newScale;
        });
    };

    const handleMouseDown = (e) => {
        // Only enable panning when zoomed in
        if (scale > 1) {
            setIsPanning(true);
            setPanStart({
                x: e.clientX - pan.x,
                y: e.clientY - pan.y,
            });
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (isPanning && scale > 1) {
            setPan({
                x: e.clientX - panStart.x,
                y: e.clientY - panStart.y,
            });
        }
    };

    const handleMouseUp = () => {
        setIsPanning(false);
    };

    // Add wheel event listener with passive: false
    useEffect(() => {
        const element = zoomWrapperRef.current;
        if (!element) return;

        element.addEventListener('wheel', handleWheelZoom, { passive: false });
        
        return () => {
            element.removeEventListener('wheel', handleWheelZoom);
        };
    }, [scale]);

    // Add global mouse move and up listeners for panning
    useEffect(() => {
        if (isPanning) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isPanning, pan, panStart, scale]);

    const imageTransformStyle = {
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
        transformOrigin: `${origin.x}% ${origin.y}%`,
        cursor: scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
    };

    // ESC behavior: exit fullscreen first, then close modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                if (isFullscreen) {
                    setIsFullscreen(false);
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isFullscreen, onClose]);

    return (
        <div className="compareModalOverlay">
            <div
                className={`compareModalContent ${isFullscreen ? "fullscreen" : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="compareModalHeader">
                    <span>Compare Images</span>
                    <div className="d-flex align-items-center gap-1">
                        <SingleSelectDropdown
                            options={RESIZE_OPTIONS}
                            selectedValue={resize}
                            keyName={"value"}
                            valueName={"pxName"}
                            onSelectItemChange={(value) =>
                                setResize(Number(value))
                            }
                            dropdownName="resolution"
                            placeholder="Select resolution"
                        />

                        <button
                            className="compareCloseBtn d-flex"
                            onClick={handleToggleFullscreen}
                            title={isFullscreen ? "Collapse" : "Expand"}
                        >
                            {isFullscreen ? <CompactIcon /> : <ExpandIcon />}
                        </button>

                        <button
                            className="compareCloseBtn d-flex"
                            onClick={onClose}
                        >
                            <Close />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="compareModalBody">
                    <div 
                        className="compareSliderWrapper zoomWrapper"
                        ref={zoomWrapperRef}
                        onMouseDown={handleMouseDown}
                        style={{ 
                            cursor: scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                            userSelect: 'none'
                        }}
                    >
                        <ReactCompareSlider
                            onlyHandleDraggable={true}
                            itemOne={
                                <ReactCompareSliderImage
                                    src={leftImageUrl}
                                    alt="Left image"
                                    style={imageTransformStyle}
                                />
                            }
                            itemTwo={
                                <ReactCompareSliderImage
                                    src={rightImageUrl}
                                    alt="Right image"
                                    style={imageTransformStyle}
                                />
                            }
                        />

                        {/* Date overlays */}
                        <div className="compareDate compareDateLeft">
                            <DateIcon />
                            {formatDate(leftDate)}
                        </div>

                        <div className="compareDate compareDateRight">
                            <DateIcon />
                            {formatDate(rightDate)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompareModal;